<template lang="html">
  <v-dialog
    scrollable
    :value="true"
    max-width="500"
    persistent
  >
    <v-form
      @submit.prevent="saveItem"
      v-model="isValid"
      ref="form"
      style="width:100%"
    >
      <v-card :class="{large: !appSmall}">
        <v-card-title
          class="headline grey lighten-2"
          primary-title
        >
          {{recordId === 'new' ? 'Create' : 'Edit'}} Scheduled Report
        </v-card-title>

        <v-card-text v-if="!initialLoading" class="pt-4">
          <v-select
            label="Report Name"
            outlined
            v-model="record.template"
            :disabled="loading"
            :rules="[...validation.required]"
            :items="reportTemplates.map(t => ({text: t.name, value: t.id}))"
          />
          <v-select
            label="Report Frequency"
            outlined
            v-model="record.timeperiod"
            :disabled="loading"
            :rules="[...validation.required]"
            :items="timePeriods.map(t => ({text: t.name, value: t.id}))"
          />
          <TimePicker
            label="Time to Send"
            outlined
            v-if="selectedTimePeriod.name === 'Daily'"
            :disabled="loading"
            :value="timeToSend"
            @input="updateTimeToSend"
            class="mb-4"
            utc
            :allowed-minutes="[0, 15, 30, 45]"
          />
          <MachinesPicker
            outlined
            :value="record.allmachines ? machines.map(m => m.id + '') : record.machines.map(m => m + '')"
            @input="updateMachines"
            :disabled="loading"
            multiple
          />
          <v-checkbox
            v-model="record.enabled"
            :disabled="loading"
            label="Enabled"
          />
        </v-card-text>

        <v-divider></v-divider>

        <v-alert :value="true" type="error" style="width:100%" v-if="error">{{error}}</v-alert>

        <v-card-actions>
          <v-btn
            v-if="!loading && !initialLoading"
            @click="$emit('close')"
            text
          >
            Cancel
          </v-btn>
          <v-spacer></v-spacer>
          <v-progress-circular
            v-if="loading  || initialLoading"
            class="ma-2"
            indeterminate
            color="primary"
          ></v-progress-circular>
          <v-spacer></v-spacer>
          <v-btn
            v-if="!loading && !initialLoading"
            color="primary"
            text
            type="submit"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<style lang="scss" scoped>
.large {
  .layout {
    .flex:first-child {
      padding-right: 8px;
    }
    .flex:last-child {
      padding-left: 8px;
    }
  }
}
</style>

<script>
import { mapState, mapActions } from 'vuex'
import cloneDeep from 'lodash/cloneDeep'
import pick from 'lodash/pick'
import get from 'lodash/get'
import ValidationMixin from '@/mixins/ValidationMixin'
import MachinesPicker from '@/components/MachinesPicker'
import TimePicker from '@/components/TimePicker'
import moment from 'moment-timezone'

export default {
  mixins: [ValidationMixin],
  components: {
    MachinesPicker,
    TimePicker
  },
  props: {
    recordId: {
      type: [String, Number]
    }
  },
  data () {
    return {
      record: {
        machines: [],
        allmachines: false
      },
      error: null,
      initialLoading: false,
      loading: false
    }
  },
  computed: {
    ...mapState(['user']),
    ...mapState('reportTemplates', {
      reportTemplatesLoading: 'loading',
      reportTemplates: 'data'
    }),
    ...mapState('timePeriods', {
      timePeriods: 'data'
    }),
    ...mapState('machines', {
      machines: 'data'
    }),
    selectedTimePeriod () {
      if (!this.record.timeperiod) {
        return {}
      }
      let timeperiod = (this.timePeriods || []).find((t) => t.id + '' === this.record.timeperiod + '')
      return timeperiod || {}
    },
    timeToSend () {
      if (!this.record.timeperioddetail) {
        return ''
      }
      let m = moment.utc(`2020-01-01 ${this.record.timeperioddetail}`, 'YYYY-MM-DD h:mm A')
      return m.utc().format('HH:mm:00')
    }
  },
  methods: {
    ...mapActions('reportTemplates', {
      findReportTemplates: 'find'
    }),
    ...mapActions('timePeriods', {
      findTimePeriods: 'find'
    }),
    ...mapActions('reports', {
      getItem: 'get',
      save: 'save'
    }),
    updateTimeToSend (value) {
      let m = moment.utc(`2020-01-01 ${value}`, 'YYYY-MM-DD HH:mm:ss')
      this.$set(this.record, 'timeperioddetail', m.utc().format('h:mm A'))
    },
    updateMachines (value) {
      if (value && value.length && value.length === this.machines.length) {
        this.record.allmachines = true
        this.record.machines = []
      } else {
        this.record.allmachines = false
        this.record.machines = value
      }
    },
    async saveItem () {
      if (!this.isValid) {
        this.$refs.form.validate()
        return
      }
      this.loading = true
      this.error = null
      if (!this.record.user) {
        this.record.user = this.user.username
      }
      if (this.selectedTimePeriod.name !== 'Daily') {
        this.record.timeperioddetail = ''
      }
      try {
        this.record = await this.save(pick(this.record, ['id', 'user', 'template', 'machines', 'enabled', 'allmachines', 'timeperiod', 'timeperioddetail']))
        this.$emit('close')
      } catch (e) {
        this.loading = false
        this.error = get(e, 'response.data') || e
        throw e
      }
    }
  },
  async created () {
    if (this.recordId) {
      this.initialLoading = true
      if (!this.timePeriods.length) {
        await this.findTimePeriods()
      }
      if (!this.reportTemplates.length) {
        await this.findReportTemplates()
      }
      try {
        let record = {
          machines: [],
          allmachines: false
        }
        if (this.recordId !== 'new') {
          record = await this.getItem(this.recordId)
        }
        this.record = cloneDeep(record)
        this.initialLoading = false
      } catch (e) {
        this.initialLoading = false
        throw e
      }
    }
  }
}
</script>
